import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Product } from '../models/product.model';
import {
  Order,
  OrderCalculation,
  CreateOrderRequest,
  CreateOrderResponse,
  InitiatePaymentResponse,
  ConfirmPaymentResponse,
  PaymentMethod
} from '../models/order.model';
import { ENVIRONMENT } from 'shared';


@Injectable({
  providedIn: 'root'
})
export class OrderService {
  private readonly apiUrl = this.environment.apiUrl || 'http://localhost:3000/api';

  constructor(private http: HttpClient, @Inject(ENVIRONMENT) private environment: any) {}

  // Get all active products. `locale` selects the UI language so language-specific
  // products (e.g. Wolfpack Assets course/bundle products) match the active
  // language; the backend falls back to the user's profile language when omitted.
  getProducts(locale?: string): Observable<{
    products: Product[],
    requires_topup?: boolean,
    topups_required?: number
  }> {
    return this.http.get<{ products: Product[] }>(`${this.apiUrl}/products`,
      locale ? { params: { locale } } : {});
  }

  // Get single product
  getProduct(id: number): Observable<Product> {
    return this.http.get<Product>(`${this.apiUrl}/products/${id}`);
  }

  // Calculate order total
  calculateOrder(items: { product_id: number; quantity: number }[]): Observable<OrderCalculation> {
    return this.http.post<OrderCalculation>(`${this.apiUrl}/orders/calculate`, { items });
  }

  // Create order
  createOrder(request: CreateOrderRequest): Observable<CreateOrderResponse> {
    return this.http.post<CreateOrderResponse>(`${this.apiUrl}/orders`, request);
  }

  // Initiate payment
  initiatePayment(orderId: number, paymentMethod: PaymentMethod): Observable<InitiatePaymentResponse> {
    return this.http.post<InitiatePaymentResponse>(
      `${this.apiUrl}/orders/${orderId}/initiate_payment`,
      { payment_method: paymentMethod }
    );
  }

  // Confirm eWallet payment
  confirmEwalletPayment(orderId: number, confirmationCode: string): Observable<ConfirmPaymentResponse> {
    return this.http.post<ConfirmPaymentResponse>(
      `${this.apiUrl}/orders/${orderId}/confirm_ewallet`,
      { confirmation_code: confirmationCode }
    );
  }

  // Update Web3 wallet address (after wallet connection)
  updateWeb3WalletAddress(orderId: number, walletAddress: string): Observable<any> {
    return this.http.patch<any>(
      `${this.apiUrl}/orders/${orderId}/update_web3_wallet`,
      { wallet_address: walletAddress }
    );
  }

  // Submit Web3 transaction (immediately after user sends tx).
  // `token` is the BSC stablecoin the customer paid in ("USDT"/"USDC").
  submitWeb3Transaction(
    orderId: number,
    transactionHash: string,
    walletAddress: string,
    token: 'USDT' | 'USDC' = 'USDT'
  ): Observable<ConfirmPaymentResponse> {
    return this.http.post<ConfirmPaymentResponse>(
      `${this.apiUrl}/orders/${orderId}/submit_web3_transaction`,
      { transaction_hash: transactionHash, wallet_address: walletAddress, token }
    );
  }

  // Check Web3 confirmation status (polling endpoint)
  getWeb3ConfirmationStatus(
    orderId: number,
    transactionHash: string
  ): Observable<ConfirmPaymentResponse> {
    return this.http.get<ConfirmPaymentResponse>(
      `${this.apiUrl}/orders/${orderId}/web3_confirmation_status`,
      { params: { transaction_hash: transactionHash } }
    );
  }

  // Confirm Stripe payment
  confirmStripePayment(orderId: number, paymentIntentId: string): Observable<ConfirmPaymentResponse> {
    return this.http.post<ConfirmPaymentResponse>(
      `${this.apiUrl}/orders/${orderId}/confirm_stripe`,
      { payment_intent_id: paymentIntentId }
    );
  }

  // Confirm PayPal payment (server-side capture of the approved PayPal order)
  confirmPaypalPayment(orderId: number, paypalOrderId: string): Observable<ConfirmPaymentResponse> {
    return this.http.post<ConfirmPaymentResponse>(
      `${this.apiUrl}/orders/${orderId}/confirm_paypal`,
      { paypal_order_id: paypalOrderId }
    );
  }

  // Confirm Authorize.Net payment (server-side charge of the Accept.js opaqueData token)
  confirmAuthorizeNetPayment(
    orderId: number,
    opaqueData: { dataDescriptor: string; dataValue: string }
  ): Observable<ConfirmPaymentResponse> {
    return this.http.post<ConfirmPaymentResponse>(
      `${this.apiUrl}/orders/${orderId}/confirm_authorize_net`,
      { data_descriptor: opaqueData.dataDescriptor, data_value: opaqueData.dataValue }
    );
  }

  // Get order details
  getOrder(orderId: number): Observable<Order> {
    return this.http.get<Order>(`${this.apiUrl}/orders/${orderId}`);
  }

  // Get user's orders (paginated)
  getUserOrders(page: number = 1, perPage: number = 20): Observable<any> {
    const params = {
      page: page.toString(),
      per_page: perPage.toString()
    };
    return this.http.get<any>(`${this.apiUrl}/orders`, { params });
  }

  // Log Web3 payment event (connection, success, failure)
  logWeb3Event(
    orderId: number,
    eventType: 'connect' | 'disconnect' | 'send' | 'success' | 'error',
    message: string,
    walletAddress?: string,
    transactionHash?: string
  ): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/orders/${orderId}/log_web3_event`,
      {
        event_type: eventType,
        message: message,
        wallet_address: walletAddress,
        transaction_hash: transactionHash
      }
    );
  }
}
